import api from "@/lib/axiosConfig";
import type { QueryClient } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type ExternalLinkOwnerType =
  | "artwork"
  | "multipleprint"
  | "multiplesample"
  | "tirage"
  | "sculptureEdition"
  | "document"
  | "photography"
  | "writing"
  | "audiovisual"
  | "collector";

export interface ExternalLinkDTO {
  id: string;
  name: string;
  url: string;
  dateConsultation: string;
  notes: string;
}

const queryKey = (type: string, ownerId: string) => ["external-links", type, ownerId];

function invalidateExternalLinkCounts(
  qc: QueryClient,
  type: ExternalLinkOwnerType,
  ownerId: string,
) {
  qc.invalidateQueries({
    queryKey: ["countAllRelatedFields", type, ownerId],
  });
}

export const useGetExternalLinks = (type: ExternalLinkOwnerType, ownerId?: string) =>
  useQuery({
    queryKey: queryKey(type, ownerId ?? ""),
    queryFn: async () => {
      const { data } = await api.get(`back/api/external-link/${type}/${ownerId}`);
      return data as ExternalLinkDTO[];
    },
    enabled: !!ownerId,
  });

export const useCreateExternalLink = (type: ExternalLinkOwnerType, ownerId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<ExternalLinkDTO>) => {
      const res = await api.post(`back/api/external-link/${type}/${ownerId}`, { data });
      return res.data as ExternalLinkDTO;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKey(type, ownerId) });
      invalidateExternalLinkCounts(qc, type, ownerId);
    },
  });
};

export const useUpdateExternalLink = (type: ExternalLinkOwnerType, ownerId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { id: string; data: Partial<ExternalLinkDTO> }) => {
      const res = await api.put(`back/api/external-link/${payload.id}`, { data: payload.data });
      return res.data as ExternalLinkDTO;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKey(type, ownerId) });
      invalidateExternalLinkCounts(qc, type, ownerId);
    },
  });
};

export const useDeleteExternalLink = (type: ExternalLinkOwnerType, ownerId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`back/api/external-link/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKey(type, ownerId) });
      invalidateExternalLinkCounts(qc, type, ownerId);
    },
  });
};
